/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from 'react';
import ClickBase from '../../../containers/SearchResultsPage/ClickBase';
import { ConnectedProps, connect } from 'react-redux';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import AnalyticsUtils from '../../../analytics/AnalyticsUtil';
import {
	setSearchQuery,
	setSubmittedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
} from '../../../actions/searchActions';
import {
	CONTENT_TYPE,
	DEEPLINK_ROUTES_PARAMS,
	SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS,
} from '../../../containers/SearchResultsPage/SearchConstants';
import SearchUtils from '../../../utils/SearchUtils';
import './SearchDropdownAppsItem.scss';
import FileTypeService from '../../../services/FileTypeService';

type OwnProps = {
	searchHostContext: HostContext;
	item: Record<string, any>;
	index: number;
	hostShowSearchDropdown: boolean;
	searchAppMetadataList: AppMetadata;
};

type State = {
	shouldShowLaunchButton: boolean;
};

/*
 * This renders the line item (App) in the Apps section in the Search Dropdown.
 */
type SearchDropdownAppsItemProps = OwnProps & WrappedComponentProps & PropsFromRedux;
class SearchDropdownAppsItem extends ClickBase<SearchDropdownAppsItemProps, State> {
	/*
	 * Executes the Search Quick Links Item Redirect to PDP Page
	 */

	constructor(props: SearchDropdownAppsItemProps) {
		super(props);
		this.state = {
			shouldShowLaunchButton: false,
		};
	}

	componentDidMount() {
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		const renderType = this.props.item.renderType;
		const appTitle = this.getAppTitle();
		const appTypeForAnalytics = SearchUtils.getAppTypeForAnalytics(this.props.item.type);
		const contentNameForAnalytics = appTypeForAnalytics ? `${appTitle} (${appTypeForAnalytics})` : appTitle;

		if (renderType === SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_SHORTCUT) {
			AnalyticsUtils.location = `IntentToAction:Shortcut:${this.props.item.id}`;
			analytics.postIntentShortcutRender(
				this.props.item,
				'content',
				this.props.searchQueryValue,
				contentNameForAnalytics,
				this.props.index,
				this.props.searchAutoCompleteIntentTerms.length,
				this.props.item.contentType || 'static',
			);
		} else {
			analytics.postQuickLinksRender(
				this.props.item,
				'content',
				'autocomplete',
				this.props.searchQueryValue,
				contentNameForAnalytics,
				this.props.index,
			);
		}

		SearchUtils.checkIfHostContextCanLaunchApp({
			hostContext: this.props.searchHostContext,
			appMetadata: this.props.item,
		})
			.then((canLaunch) => {
				this.setState({
					shouldShowLaunchButton: canLaunch,
				});
			})
			.catch(() => {
				this.setState({
					shouldShowLaunchButton: false,
				});
			});
	}

	onClick(event: Record<string, any>, index: number, item: Record<string, any>, searchQueryString: string) {
		if (event.which === 13 || event.keyCode === 13 || event.type === 'click') {
			const pathName = window.location && window.location.pathname;
			const isSearchRoute = pathName ? pathName.indexOf('search') > -1 : false;
			let openUrl: string | undefined = undefined;

			if (item.url !== undefined) {
				openUrl = item.url;
			} else if (item.nonCC) {
				openUrl = item?.ctas?.length > 1 ? item?.ctas[1]?.link : item?.url;
			} else {
				openUrl = this.props.searchHostContext.environment !== 'prod' ? item.appStageURL : item.appProdURL;
			}

			// Uses the ClickResult flow type
			const appResultItem: ClickResult = {
				action: 'open',
				// $FlowFixMe - doesn't have AssetType since it is an app
				item: {
					id: item.id,
					// @ts-ignore
					type: this.props.item.appType,
					attributes: {
						name: this.getAppTitle(),
						modificationDate: Date.now(),
						creationDate: Date.now(),
						fileSize: 0,
						assetCategory: this.props.item.appType as AssetCategory,
						openUrl,
						openInSameTab: item.actionOpenUrlInSameTab,
						nonCC: item.nonCC,
					},
				},
			};

			const analytics = new SearchAnalytics(this.props.searchHostContext);

			if (this.props.item.renderType === SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_SHORTCUT) {
				const appTitle = this.getAppTitle();
				const appTypeForAnalytics = SearchUtils.getAppTypeForAnalytics(this.props.item.type);
				const contentNameForAnalytics = appTypeForAnalytics ? `${appTitle} (${appTypeForAnalytics})` : appTitle;
				analytics.postIntentShortcutSelection(
					{
						...appResultItem.item,
						...item,
						analyticsUrl: AnalyticsUtils.appendUrlWithAnalytics(appResultItem.item.attributes.openUrl),
					},
					this.props.item.sapCode || this.props.item.ffcId ? CONTENT_TYPE.LEARN_MORE : 'content',
					searchQueryString,
					contentNameForAnalytics,
					index,
					this.props.searchAutoCompleteIntentTerms.length,
					this.props.item.contentType || 'static',
				);
			} else {
				// log quick links click event to ingest
				analytics.postQuickLinksDropDownSelection(
					`${this.getAppTitle()} (${SearchUtils.getAppTypeForAnalytics(this.props.item.type)})`,
					appResultItem.item.attributes.openUrl,
					index,
					'content',
					'autocomplete',
					searchQueryString,
				);

				analytics.postLaunchOfApp(
					`${this.getAppTitle()} (${SearchUtils.getAppTypeForAnalytics(this.props.item.type)})`,
					this.props.item.type === 'Service' ? this.props.item.serviceLaunchUrl : this.props.item.url,
					CONTENT_TYPE.LEARN_MORE,
					SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_QUICK_LINK,
					this.props.searchQueryValue,
				);
			}

			const exitButtonElem = document.getElementById('search-exit-button');

			if (exitButtonElem && !isSearchRoute) {
				analytics.initSearchAnalytics(item.label);
				exitButtonElem.click();
			}

			const deeplink = item?.deeplink ?? false;
			const deeplinkParams = item?.actionDeeplinkParams;

			if (deeplink === true && deeplinkParams) {
				SearchUtils.routeDeepLinksInCCD({
					searchHostContext: this.props.searchHostContext,
					deepLinkParams: deeplinkParams,
					itemType: this.props.item.appType,
					templateParams: {
						searchTerm: this.props.searchQueryValue,
					},
				}).then((routedInCCD) => {
					if (!routedInCCD) {
						this.onClickCard(this.props.searchHostContext, this.props.item.appType, appResultItem);
					}
				});
			} else {
				this.onClickCard(this.props.searchHostContext, this.props.item.appType, appResultItem);
			}

			// Fix for https://jira.corp.adobe.com/browse/CCS-1414
			// SearchBar.js onSearchBarWrapperClick registers for click events on the bar and dropdown.
			// We need to make sure Quick Links events don't propagate to the bar to avoid onSearchBarWrapperClick
			// reassigning focus to the search bar.
			event.stopPropagation();
		}
	}

	/*
	 * Returns the appropriate app title
	 * @return {string}
	 */
	getAppTitle() {
		let appTitle = `${this.props.item.name}`;
		const appType = SearchUtils.getAppTypeLocalizedKey(this.props.item.type);

		if (appType) {
			appTitle = `${this.props.item.name}`;
		}

		return appTitle;
	}

	/*
	 * Returns the appropriate app type
	 * @return {string}
	 */
	getAppType() {
		let appType = SearchUtils.getLocalizedAppType(this.props.item.type);

		if (appType) {
			appType = `${this.props.intl.formatMessage({
				id: appType,
			})}`;
		}

		return appType;
	}

	getIcon(): string {
		const { appType, sapCode, ffcId, iconUrls } = this.props.item;
		if (sapCode || ffcId) {
			return SearchUtils.getAppIconUrl(iconUrls);
		} else if (iconUrls && iconUrls.length) {
			const [lightModeIcon, darkModeIcon] = iconUrls;
			return SearchUtils.isDarkMode(this.props.theme)
				? darkModeIcon || lightModeIcon
				: lightModeIcon || darkModeIcon;
		}
		return SearchUtils.getGenericRenditions(appType);
	}

	/*
	 * Execute 'launchApp' method
	 */
	executeAppLaunch(shouldShowOpenButton, event: Record<string, any>) {
		if (event.which === 13 || event.keyCode === 13 || event.type === 'click') {
			if (shouldShowOpenButton && typeof this.props.searchHostContext.launchApp === 'function') {
				SearchUtils.executeAppLaunch({
					searchHostContext: this.props.searchHostContext,
					appMetadata: this.props.item,
					searchAppMetadataList: this.props.searchAppMetadataList,
				});
				event.stopPropagation();
				const analytics = new SearchAnalytics(this.props.searchHostContext);
				let eventSubType;
				if (this.props.item.sapCode || this.props.item.ffcId) {
					if (this.props.item.type === 'Service') {
						eventSubType = 'Launch';
					} else {
						eventSubType = 'Open';
					}
				} else {
					eventSubType = 'content';
				}
				if (this.props.item.renderType === SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_SHORTCUT) {
					analytics.postIntentShortcutSelection(
						{
							...this.props.item,
							analyticsUrl: AnalyticsUtils.appendUrlWithAnalytics(this.props.item.url),
						},
						eventSubType,
						this.props.searchQueryValue,
						this.props.item.name,
						this.props.index,
						this.props.searchAutoCompleteIntentTerms.length,
						this.props.item.contentType || 'static',
					);
				} else {
					// log quick links click event to ingest
					analytics.postQuickLinksDropDownSelection(
						`${this.getAppTitle()} (${SearchUtils.getAppTypeForAnalytics(this.props.item.type)})`,
						this.props.item.url,
						this.props.index,
						'content',
						'autocomplete',
						this.props.searchQueryValue,
					);

					analytics.postLaunchOfApp(
						`${this.getAppTitle()} (${SearchUtils.getAppTypeForAnalytics(this.props.item.type)})`,
						this.props.item.type === 'Service' ? this.props.item.serviceLaunchUrl : this.props.item.url,
						this.props.item.type === 'Service' ? 'Launch' : 'Open',
						SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_QUICK_LINK,
						this.props.searchQueryValue,
					);
				}
			} else {
				this.onClick(event, this.props.index, this.props.item, this.props.searchQueryValue);
			}
		}
	}

	render() {
		let buttonString =
			this.props.item.actionLabel ||
			this.props.intl.formatMessage({
				id: 'app.search.learn.more',
			});

		if (this.state.shouldShowLaunchButton) {
			buttonString = this.props.intl.formatMessage({
				id: 'app.search.open',
			});
			if (this.props.item.type === 'Service') {
				buttonString = this.props.intl.formatMessage({
					id: 'app.search.launch',
				});
			}
			if (this.props.item.renderType === SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_SHORTCUT) {
				buttonString = this.props.item.actionLabel || buttonString;
			}
		}

		return (
			<div
				className='search-dropdown-apps-item'
				id={`search-dropdown-apps-item-${this.props.index}`}
				tabIndex={-1}
				onClick={(e) => this.executeAppLaunch(this.state.shouldShowLaunchButton, e)}
				role='menuitem'
			>
				<div className='search-dropdown-apps-item-text-container'>
					<div className='search-dropdown-apps-item-icon-container'>
						<img className='search-dropdown-apps-item-icon' alt='' src={this.getIcon()} />
					</div>

					<div className='search-dropdown-apps-item-text-title-container'>
						<div className='search-dropdown-apps-item-text-title'>
							<span>{this.getAppTitle()}</span>
						</div>
						<div className='search-dropdown-apps-item-text-type'>
							<span>{`${this.getAppType() || this.props.item.description}`}</span>
						</div>
					</div>
					<span className='search-dropdown-apps-item-text-launch-button'>{buttonString}</span>
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	searchQueryValue: search.searchQuery,
	searchAutoCompleteItemsValue: search.searchAutoCompleteItems,
	searchAutoCompleteIntentTerms: search.searchAutoCompleteIntentTerms,
	searchAppMetadataList: search.searchAppMetadataList,
	theme: search.theme,
});

const mapDispatchToProps = {
	setSubmittedSearchQuery,
	setSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropdownAppsItem));
